// Validation utilities for preventing gibberish/irrelevant data
// Being lenient but catching obvious spam/nonsense

export const validateText = (text, field = 'field') => {
  if (!text || text.trim().length === 0) {
    return { isValid: false, message: `${field} is required` };
  }

  const trimmed = text.trim();
  
  // Check for minimum length
  if (trimmed.length < 2) {
    return { isValid: false, message: `${field} must be at least 2 characters` };
  }

  // Check for excessive special characters or numbers (might indicate spam)
  const specialCharRatio = (trimmed.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/g) || []).length / trimmed.length;
  if (specialCharRatio > 0.5) {
    return { isValid: false, message: `${field} contains too many special characters` };
  }

  // Check for excessive numbers in text fields (except test scores)
  const numberRatio = (trimmed.match(/\d/g) || []).length / trimmed.length;
  if (numberRatio > 0.6 && !field.toLowerCase().includes('score') && !field.toLowerCase().includes('gpa')) {
    return { isValid: false, message: `${field} contains too many numbers` };
  }

  // Check for repeated characters (like "aaaaaaa" or "1111111")
  const repeatedChars = trimmed.match(/(.)\1{4,}/g);
  if (repeatedChars && repeatedChars.length > 0) {
    return { isValid: false, message: `${field} contains repeated characters` };
  }

  // Check for keyboard mashing patterns
  const keyboardPatterns = [
    /qwerty/i, /asdf/i, /zxcv/i, /1234/i, /abcd/i,
    /qwertyuiop/i, /asdfghjkl/i, /zxcvbnm/i
  ];
  
  for (const pattern of keyboardPatterns) {
    if (pattern.test(trimmed)) {
      return { isValid: false, message: `${field} appears to contain keyboard mashing` };
    }
  }

  return { isValid: true };
};

// General validation function for any input data
export const validateInput = (data) => {
  const errors = [];
  
  if (!data || data.trim().length === 0) {
    errors.push('Data is required');
  }
  
  try {
    // If it's JSON, parse and validate the structure
    const parsed = JSON.parse(data);
    
    // Check for obviously invalid data patterns
    const stringified = JSON.stringify(parsed).toLowerCase();
    
    // Patterns that indicate spam or invalid data
    const invalidPatterns = [
      /test{3,}/g,           // "testtest" or "testtesttest"
      /spam/g,               // spam content
      /xxx{2,}/g,            // "xxx" or "xxxx"
      /\.{5,}/g,             // excessive periods
      /fake/g,               // "fake" content
      /dummy/g,              // "dummy" content
      /asdf{2,}/g,           // keyboard mashing
      /qwerty/g,             // keyboard patterns
      /1{5,}/g,              // excessive repeated numbers
      /a{10,}/g              // excessive repeated letters
    ];
    
    for (const pattern of invalidPatterns) {
      if (pattern.test(stringified)) {
        errors.push('Data appears to contain invalid or spam content');
        break;
      }
    }
    
  } catch (e) {
    // If it's not JSON, validate as plain text
    const textValidation = validateText(data, 'Input');
    if (!textValidation.isValid) {
      errors.push(textValidation.message);
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

export const validateSchoolName = (schoolName) => {
  const basicValidation = validateText(schoolName, 'School name');
  if (!basicValidation.isValid) return basicValidation;

  const trimmed = schoolName.trim();
  
  // Check for reasonable length
  if (trimmed.length < 3 || trimmed.length > 100) {
    return { isValid: false, message: 'School name should be between 3-100 characters' };
  }

  // Must contain at least one letter
  if (!/[a-zA-Z]/.test(trimmed)) {
    return { isValid: false, message: 'School name must contain letters' };
  }

  // Common patterns that might indicate fake schools
  const spamPatterns = [
    /fake/i, /test/i, /dummy/i, /spam/i,
    /^x+$/i, /^\.+$/i, /123/i
  ];
  
  for (const pattern of spamPatterns) {
    if (pattern.test(trimmed)) {
      return { isValid: false, message: 'Please enter a valid school name' };
    }
  }

  return { isValid: true };
};

export const validateMajor = (major) => {
  const basicValidation = validateText(major, 'Major');
  if (!basicValidation.isValid) return basicValidation;

  const trimmed = major.trim();
  
  // Check for reasonable length
  if (trimmed.length < 2 || trimmed.length > 50) {
    return { isValid: false, message: 'Major should be between 2-50 characters' };
  }

  // Must contain at least one letter
  if (!/[a-zA-Z]/.test(trimmed)) {
    return { isValid: false, message: 'Major must contain letters' };
  }

  // Common spam patterns
  const spamPatterns = [
    /^x+$/i, /^\.+$/i, /test/i, /fake/i, /dummy/i, /spam/i
  ];
  
  for (const pattern of spamPatterns) {
    if (pattern.test(trimmed)) {
      return { isValid: false, message: 'Please enter a valid major' };
    }
  }

  return { isValid: true };
};

export const validateGPA = (gpa) => {
  if (!gpa) {
    return { isValid: false, message: 'GPA is required' };
  }

  const trimmed = gpa.trim();

  // Allow text formats like "First Class Honours", "Second Class Upper", etc.
  const textGradeFormats = [
    /first\s+class/i, /second\s+class/i, /third\s+class/i,
    /honours?/i, /distinction/i, /merit/i, /pass/i,
    /summa\s+cum\s+laude/i, /magna\s+cum\s+laude/i, /cum\s+laude/i,
    /dean'?s\s+list/i
  ];

  // Check if it's a text-based grade
  const isTextGrade = textGradeFormats.some(pattern => pattern.test(trimmed));
  
  if (isTextGrade) {
    return { isValid: true };
  }

  // For numeric GPAs, be more flexible
  const numericGPA = parseFloat(trimmed);
  
  if (isNaN(numericGPA)) {
    return { isValid: false, message: 'GPA must be a number or valid grade format (e.g., First Class Honours)' };
  }

  // Allow reasonable GPA ranges for different scales
  // 4.0 scale: 0.0-4.0, 5.0 scale: 0.0-5.0, 10.0 scale: 0.0-10.0, 100 scale: 0-100
  if (numericGPA < 0 || numericGPA > 100) {
    return { isValid: false, message: 'GPA must be a reasonable value (0-4.0, 0-10, 0-100, or text like "First Class")' };
  }

  return { isValid: true };
};

export const validateTestScore = (score, testType) => {
  if (!score) {
    return { isValid: false, message: 'Test score is required' };
  }

  const numericScore = parseInt(score);
  
  if (isNaN(numericScore)) {
    return { isValid: false, message: 'Test score must be a number' };
  }

  // Validate based on test type
  if (testType === 'GMAT') {
    if (numericScore < 200 || numericScore > 805) {
      return { isValid: false, message: 'GMAT score must be between 200-805 (covers both Classic and Focus editions)' };
    }
  } else if (testType === 'GRE') {
    if (numericScore < 260 || numericScore > 340) {
      return { isValid: false, message: 'GRE score must be between 260-340' };
    }
  }

  return { isValid: true };
};

export const validateNationality = (nationality) => {
  const basicValidation = validateText(nationality, 'Nationality');
  if (!basicValidation.isValid) return basicValidation;

  const trimmed = nationality.trim();
  
  // Check for reasonable length
  if (trimmed.length < 3 || trimmed.length > 50) {
    return { isValid: false, message: 'Nationality should be between 3-50 characters' };
  }

  // Must contain at least one letter
  if (!/[a-zA-Z]/.test(trimmed)) {
    return { isValid: false, message: 'Nationality must contain letters' };
  }

  // Common spam patterns
  const spamPatterns = [
    /^x+$/i, /^\.+$/i, /test/i, /fake/i, /dummy/i, /spam/i,
    /^none$/i, /^n\/a$/i, /^na$/i
  ];
  
  for (const pattern of spamPatterns) {
    if (pattern.test(trimmed)) {
      return { isValid: false, message: 'Please enter a valid nationality' };
    }
  }

  return { isValid: true };
}; 